/**
 * @file tableViewStore.ts
 * @description Cross-view persistence for Squad Planning sort state (SE-5352).
 *
 * The Table and Columns views unmount when the user toggles between them, so
 * sort state held in component-local `useState` was lost on every switch.
 * These reactive vars live at module scope (same pattern as
 * `transitionsOnlyVar` in scenarioStore), letting each view restore its last
 * sort when it remounts. State is intentionally NOT keyed by scenario — the
 * ticket's AC only requires survival across view switches.
 * @module features/squad-planning/stores/tableViewStore
 */

import { makeVar, useReactiveVar } from "@apollo/client";
import { useCallback } from "react";

import type { SortingState, Updater } from "@tanstack/react-table";

import type { ColumnSortMetric, ViewMode } from "../types";

/** Column-view sort selection for one tier. */
export interface ColumnTierSort {
  /** Metric the tier is sorted by. */
  readonly metric: ColumnSortMetric;
  /** Direction applied to the metric. */
  readonly direction: "asc" | "desc";
}

/** TanStack sorting per tier for the Table view. */
const tableSortingByTierVar = makeVar<Readonly<Record<string, SortingState>>>(
  {}
);

/** Column-view metric/direction per tier. */
const columnSortByTierVar = makeVar<
  Readonly<Record<string, ColumnTierSort | undefined>>
>({});

/**
 * Shared empty sorting state for tiers the user has never sorted.
 *
 * MUST be a stable module-level reference. TanStack Table memoises
 * `getSortedRowModel` on the identity of `state.sorting`; handing it a fresh
 * `[]` on every render makes that memo miss every time, so the row model is
 * rebuilt on each pass. Leaving the Table view then re-renders the table one
 * last time and the rebuild loop never settles — the tab locks up hard
 * (SE-6233: `table.getRowModel()` was the isolated trigger).
 */
const EMPTY_SORTING: SortingState = [];

/**
 * Persisted TanStack sorting state for one Table-view tier.
 * @param tierName - Tier whose sorting is being read/written
 * @returns The tier's sorting state (empty until the user sorts) and a
 * TanStack-compatible setter accepting a value or updater function
 */
export function useTableSorting(
  tierName: string
): readonly [SortingState, (updater: Updater<SortingState>) => void] {
  const sorting =
    useReactiveVar(tableSortingByTierVar)[tierName] ?? EMPTY_SORTING;

  const setSorting = useCallback(
    (updater: Updater<SortingState>) => {
      const all = tableSortingByTierVar();
      const current = all[tierName] ?? EMPTY_SORTING;
      const next = typeof updater === "function" ? updater(current) : updater;
      tableSortingByTierVar({ ...all, [tierName]: next });
    },
    [tierName]
  );

  return [sorting, setSorting] as const;
}

/**
 * Persisted Columns-view sort selection for one tier.
 * @param tierName - Tier whose sort selection is being read/written
 * @returns The tier's stored selection (undefined until the user sorts) and
 * a setter
 */
export function useColumnTierSort(
  tierName: string
): readonly [ColumnTierSort | undefined, (sort: ColumnTierSort) => void] {
  const sort = useReactiveVar(columnSortByTierVar)[tierName];

  const setSort = useCallback(
    (next: ColumnTierSort) => {
      columnSortByTierVar({ ...columnSortByTierVar(), [tierName]: next });
    },
    [tierName]
  );

  return [sort, setSort] as const;
}

/**
 * Active Planning view (Columns / Table / Actions overview).
 *
 * Shared rather than component-local because the V1.6 design puts the view
 * toggle in the full-width actions bar above the KPI band, while the content
 * it switches lives inside the player panel — two sibling subtrees that must
 * agree on one value.
 */
export const viewModeVar = makeVar<ViewMode>("column");

/**
 * Reads and writes the active Planning view mode.
 * @returns The current view mode and a setter
 */
export function useViewMode(): readonly [ViewMode, (next: ViewMode) => void] {
  const viewMode = useReactiveVar(viewModeVar);

  const setViewMode = useCallback((next: ViewMode) => {
    viewModeVar(next);
  }, []);

  return [viewMode, setViewMode] as const;
}

/**
 * Resets every var to its initial state. Test-only — keeps specs
 * order-independent now that this state outlives component unmounts.
 */
export function resetTableViewStoreForTests(): void {
  tableSortingByTierVar({});
  columnSortByTierVar({});
  viewModeVar("column");
}
